/**
 * v-tooltip directive
 *
 * Renders a tooltip bubble appended to <body> so it is never clipped
 * by ancestor overflow containers.
 *
 * Usage:  <span v-tooltip="'Some helpful text'">hover me</span>
 */

const ARROW_SIZE = 6; // px — must match CSS border-width on the arrow
const GAP = 4;        // extra space between element and tooltip

function createTooltipEl() {
    const tip = document.createElement('div');
    tip.className = 'fusion-tooltip-body';
    tip.setAttribute('role', 'tooltip');

    const arrow = document.createElement('div');
    arrow.className = 'fusion-tooltip-arrow';
    tip.appendChild(arrow);

    const content = document.createElement('div');
    content.className = 'fusion-tooltip-content';
    tip.appendChild(content);

    document.body.appendChild(tip);
    return tip;
}

function positionTooltip(el, tip) {
    const rect = el.getBoundingClientRect();
    const tipRect = tip.getBoundingClientRect();

    // Default: above, centred
    let top = rect.top - tipRect.height - ARROW_SIZE - GAP;
    let left = rect.left + rect.width / 2 - tipRect.width / 2;

    // If it would go above the viewport, flip below
    if (top < 4) {
        top = rect.bottom + ARROW_SIZE + GAP;
        tip.classList.add('fusion-tooltip-below');
    } else {
        tip.classList.remove('fusion-tooltip-below');
    }

    // Clamp horizontally within viewport
    const margin = 8;
    if (left < margin) left = margin;
    if (left + tipRect.width > window.innerWidth - margin)
        left = window.innerWidth - margin - tipRect.width;

    tip.style.top = `${top}px`;
    tip.style.left = `${left}px`;
}

function show(el) {
    const tip = el._tooltipEl;
    if (!tip) return;
    tip.querySelector('.fusion-tooltip-content').textContent = el._tooltipText || '';
    tip.style.display = 'block';
    // Allow one frame for the browser to calculate dimensions
    requestAnimationFrame(() => positionTooltip(el, tip));
}

function hide(el) {
    const tip = el._tooltipEl;
    if (!tip) return;
    tip.style.display = 'none';
}

export default {
    mounted(el, binding) {
        el._tooltipText = binding.value || '';
        el._tooltipEl = createTooltipEl();
        el._tooltipShow = () => show(el);
        el._tooltipHide = () => hide(el);
        el.addEventListener('mouseenter', el._tooltipShow);
        el.addEventListener('mouseleave', el._tooltipHide);
        el.addEventListener('focus', el._tooltipShow);
        el.addEventListener('blur', el._tooltipHide);
    },

    updated(el, binding) {
        el._tooltipText = binding.value || '';
    },

    unmounted(el) {
        el.removeEventListener('mouseenter', el._tooltipShow);
        el.removeEventListener('mouseleave', el._tooltipHide);
        el.removeEventListener('focus', el._tooltipShow);
        el.removeEventListener('blur', el._tooltipHide);
        if (el._tooltipEl) {
            el._tooltipEl.remove();
            el._tooltipEl = null;
        }
    }
};
